import React from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { X, Star, Edit, Calendar, User } from 'lucide-react';
import { isDarkAtom } from '../../../atoms/shared/uiAtoms';
import { Character } from '../../../types/character';

interface CharacterDetailModalProps {
  character: Character;
  isOpen: boolean;
  onClose: () => void;
  onUse: (characterId: string) => void;
  onEdit?: (characterId: string) => void;
  currentUserId?: string;
}

/**
 * 角色卡详情弹窗组件
 */
export const CharacterDetailModal: React.FC<CharacterDetailModalProps> = ({
  character,
  isOpen,
  onClose,
  onUse,
  onEdit,
  currentUserId,
}) => {
  const { t } = useTranslation(['v3/character', 'common']);
  const [isDark] = useAtom(isDarkAtom);

  if (!isOpen) return null;

  // 检查是否可以查看完整提示词
  const canViewPrompt = () => {
    // 私有角色卡和官方角色卡总是可以查看
    if (character.type !== 'public') {
      return true;
    }

    // 如果是创建者本人，可以查看
    if (character.creatorId === currentUserId) {
      return true;
    }

    // 如果角色卡设置为允许查看提示词，可以查看
    return character.allowPromptVisible;
  };

  // 处理背景点击关闭
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 处理使用角色卡
  const handleUse = () => {
    onUse(character.id);
    onClose();
  };

  // 处理编辑角色卡
  const handleEdit = () => {
    if (onEdit) {
      onEdit(character.id);
      onClose();
    }
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // 获取状态显示
  const getStatusDisplay = () => {
    if (character.type === 'official') return null;
    
    switch (character.status) {
      case 'pending':
        return (
          <span className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-ios-dark-orange text-white' : 'bg-orange-100 text-orange-800'}`}>
            {t('v3/character:statusPending', '审核中')}
          </span>
        );
      case 'rejected':
        return (
          <span className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-ios-dark-red text-white' : 'bg-red-100 text-red-800'}`}>
            {t('v3/character:statusRejected', '已拒绝')}
          </span>
        );
      default:
        return null;
    }
  };

  // 获取类型标签
  const getTypeLabel = () => {
    switch (character.type) {
      case 'official':
        return (
          <span className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-ios-dark-blue text-white' : 'bg-blue-100 text-blue-800'}`}>
            {t('v3/character:typeOfficial', '官方')}
          </span>
        );
      case 'public':
        return (
          <span className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-ios-dark-green text-white' : 'bg-green-100 text-green-800'}`}>
            {t('v3/character:typePublic', '公开')}
          </span>
        );
      case 'private':
        return (
          <span className={`px-3 py-1 text-sm rounded-full ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-100 text-gray-800'}`}>
            {t('v3/character:typePrivate', '私有')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className={`
        w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl
        ${isDark ? 'bg-ios-dark-gray6' : 'bg-white'}
        ${isDark ? 'shadow-ios-dark' : 'shadow-ios-light'}
      `}>
        {/* 头部 */}
        <div className={`sticky top-0 flex items-center justify-between p-6 border-b ${isDark ? 'bg-ios-dark-gray6 border-ios-dark-separator' : 'bg-white border-ios-light-separator'}`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
            {t('v3/character:characterDetail', '角色卡详情')}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full ${isDark ? 'hover:bg-ios-dark-gray5' : 'hover:bg-gray-100'} transition-colors`}
          >
            <X size={20} />
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6 space-y-6">
          {/* 基本信息 */}
          <div className="flex items-start space-x-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label' : 'bg-gray-200 text-gray-700'}`}>
              {character.avatarUrl ? (
                <img 
                  src={character.avatarUrl} 
                  alt={character.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                character.name.charAt(0).toUpperCase()
              )}
            </div>
            
            <div className="flex-1">
              <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.name}
              </h3>
              
              <div className="flex items-center space-x-2 mb-3">
                {getTypeLabel()}
                {getStatusDisplay()}
              </div>
              
              <p className={`text-base ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {character.description}
              </p>
            </div>
          </div>

          {/* 统计信息 */}
          <div className={`grid grid-cols-4 gap-4 p-4 rounded-xl ${isDark ? 'bg-ios-dark-gray5' : 'bg-gray-50'}`}>
            <div className="text-center">
              <div className={`text-2xl font-bold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.popularityScore}
              </div>
              <div className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:popularity', '人气值')}
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.usageCount}
              </div>
              <div className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:usageCount', '使用次数')}
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.messageCount}
              </div>
              <div className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:messageCount', '对话数')}
              </div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.conversationPrompt?.length || 0}
              </div>
              <div className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:promptLength', '提示词字符')}
              </div>
            </div>
          </div>

          {/* 创建信息 */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <User size={16} className={isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'} />
              <span className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:createdBy', '创建者')}:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {character.type === 'official' 
                  ? t('v3/character:officialCreator', '官方')
                  : character.creatorName || t('v3/character:unknownCreator', '未知用户')
                }
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Calendar size={16} className={isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'} />
              <span className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:createdAt', '创建时间')}:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
                {formatDate(character.createdAt)}
              </span>
            </div>
          </div>

          {/* 提示词内容 */}
          <div className="space-y-4">
            <h4 className={`text-lg font-semibold ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'}`}>
              {t('v3/character:promptContent', '提示词内容')}
            </h4>

            {canViewPrompt() ? (
              <div className="space-y-3">
                {/* 对话提示词 */}
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'} block mb-2`}>
                    {t('v3/character:conversationPrompt', '对话提示词')}
                  </label>
                  <div className={`p-3 rounded-lg border ${isDark ? 'bg-ios-dark-gray5 border-ios-dark-separator text-ios-dark-secondary-label' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                    <p className="text-sm whitespace-pre-wrap">
                      {character.conversationPrompt || t('v3/character:noPrompt', '未设置')}
                    </p>
                  </div>
                </div>

                {/* NOTE: 图像提示词功能暂时不暴露给用户 */}
                {/* 图像提示词（如果有的话） */}
                {/* {(character.imageScenePrompt || character.imageKeywordsPositive || character.imageKeywordsNegative) && (
                  <div>
                    <label className={`text-sm font-medium ${isDark ? 'text-ios-dark-label' : 'text-ios-light-label'} block mb-2`}>
                      {t('v3/character:imagePrompts', '图像提示词')}
                    </label>
                    <div className="space-y-2">
                      {character.imageScenePrompt && (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-ios-dark-gray5 border-ios-dark-separator' : 'bg-gray-50 border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-500'}`}>
                            {t('v3/character:imageScenePrompt', '场景描述')}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-700'}`}>
                            {character.imageScenePrompt}
                          </p>
                        </div>
                      )}
                      {character.imageKeywordsPositive && (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-ios-dark-gray5 border-ios-dark-separator' : 'bg-gray-50 border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-500'}`}>
                            {t('v3/character:imageKeywordsPositive', '正面关键词')}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-700'}`}>
                            {character.imageKeywordsPositive}
                          </p>
                        </div>
                      )}
                      {character.imageKeywordsNegative && (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-ios-dark-gray5 border-ios-dark-separator' : 'bg-gray-50 border-gray-200'}`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-500'}`}>
                            {t('v3/character:imageKeywordsNegative', '负面关键词')}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-700'}`}>
                            {character.imageKeywordsNegative}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )} */}
              </div>
            ) : (
              <div className={`p-4 rounded-lg border-2 border-dashed ${isDark ? 'border-ios-dark-separator bg-ios-dark-gray5' : 'border-gray-300 bg-gray-50'}`}>
                <div className="text-center">
                  <p className={`text-sm font-medium ${isDark ? 'text-ios-dark-secondary-label' : 'text-gray-500'}`}>
                    {t('v3/character:promptNotVisible', '创建者已设置提示词不可见')}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-ios-dark-tertiary-label' : 'text-gray-400'}`}>
                    {t('v3/character:promptNotVisibleDescription', '您只能查看角色卡的基本信息，无法查看具体的提示词内容')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 底部操作按钮 */}
        <div className={`sticky bottom-0 p-6 border-t ${isDark ? 'bg-ios-dark-gray6 border-ios-dark-separator' : 'bg-white border-ios-light-separator'}`}>
          <div className="flex space-x-3">
            <button
              onClick={handleUse}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${isDark ? 'bg-ios-dark-blue text-white hover:bg-blue-600' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
            >
              {t('v3/character:useCharacter', '使用该角色卡')}
            </button>
            
            {onEdit && (
              <button
                onClick={handleEdit}
                className={`px-4 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-label hover:bg-ios-dark-gray3' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <Edit size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
